import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, TrendingUp, Heart, Camera, Users, Star, Flag } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

const DIM_LABELS = {
  facilities: "Fasilitas",
  safety: "Keamanan",
  curriculum: "Kurikulum",
  value_for_money: "Value for money",
  student_happiness: "Kebahagiaan siswa",
  well_rounded: "Pengembangan holistik",
  communication: "Komunikasi sekolah",
};

function KPI({ label, value, icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-[#EAE7E0] bg-white p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">{label}</div>
          <div className="text-3xl font-serif text-[#1F2937] mt-1 tabular-nums">{value ?? "—"}</div>
        </div>
        <div className="w-9 h-9 rounded-full bg-[#EFF6FF] flex items-center justify-center text-[#1E3A8A]">
          {icon}
        </div>
      </div>
    </div>
  );
}

export default function SchoolAnalytics() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !["admin", "superadmin", "moderator"].includes(user.role)) return;
    setLoading(true);
    api.get(`/admin/schools/${id}/analytics`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Gagal memuat analytics"))
      .finally(() => setLoading(false));
  }, [id, user]);

  const viewsData = useMemo(() => (data?.views_last_30d || []).map((d) => ({ ...d, label: d.date.slice(5) })), [data]);
  const npsData = useMemo(() => (data?.nps_trend || []).map((n) => ({ ...n, label: n.week.replace(/^\d{4}-/, "") })), [data]);
  const ratingBars = useMemo(() => Object.entries(data?.avg_ratings || {}).map(([k, v]) => ({ dim: DIM_LABELS[k] || k, value: v })), [data]);

  if (!user) return <div className="p-10 text-center text-[#4B5563]">Silakan login.</div>;
  if (!["admin", "superadmin", "moderator"].includes(user.role)) return <div className="p-10 text-center text-[#4B5563]">Akses ditolak.</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="school-analytics-page">
      <Link to="/admin" className="text-sm text-[#4B5563] hover:text-[#1E3A8A] inline-flex items-center gap-1.5 mb-4" data-testid="analytics-back">
        <ArrowLeft size={14} /> Kembali ke dasbor
      </Link>
      <div className="text-xs uppercase tracking-widest text-[#4B5563]">ANALYTICS SEKOLAH</div>
      <div className="flex items-center gap-3 mt-1">
        <h1 className="text-3xl sm:text-4xl font-serif text-[#1F2937]">{data?.school?.name || "…"}</h1>
        {data?.school?.tier && data.school.tier !== "free" && (
          <span className="px-2.5 py-1 rounded-full bg-[#FEF3C7] text-[#92400E] text-[10px] uppercase tracking-widest">{data.school.tier}</span>
        )}
      </div>

      {loading ? (
        <div className="mt-10 text-center text-[#4B5563]" data-testid="analytics-loading">Memuat data…</div>
      ) : !data ? (
        <div className="mt-10 text-center text-[#4B5563]">Tidak ada data.</div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI testid="kpi-views" label="Views 30 hari" value={data.total_views_30d.toLocaleString("id-ID")} icon={<TrendingUp size={16} />} />
            <KPI testid="kpi-total-views" label="Total views" value={(data.total_views_all_time || 0).toLocaleString("id-ID")} icon={<Users size={16} />} />
            <KPI testid="kpi-likes" label="Like foto" value={data.photo_likes_total} icon={<Heart size={16} />} />
            <KPI testid="kpi-photos" label="Foto galeri" value={data.photos_total} icon={<Camera size={16} />} />
            <KPI testid="kpi-reviews" label="Review" value={data.reviews_total} icon={<Star size={16} />} />
            <KPI testid="kpi-nps" label="NPS saat ini" value={data.nps_current === null ? "—" : data.nps_current} icon={<TrendingUp size={16} />} />
            <KPI testid="kpi-reports" label="Laporan foto" value={data.photo_reports_total} icon={<Flag size={16} />} />
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 rounded-2xl border border-[#EAE7E0] bg-white p-5" data-testid="chart-views">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-[#1F2937]">Views harian (30 hari)</div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">unique per hari</div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={viewsData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE7E0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#4B5563" }} interval={4} />
                  <YAxis tick={{ fontSize: 10, fill: "#4B5563" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EAE7E0", fontSize: 12 }} />
                  <Line type="monotone" dataKey="count" stroke="#1E3A8A" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-2xl border border-[#EAE7E0] bg-white p-5" data-testid="chart-nps">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-[#1F2937]">Tren NPS 8 minggu</div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">skala -100 s/d 100</div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={npsData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE7E0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#4B5563" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#4B5563" }} domain={[-100, 100]} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EAE7E0", fontSize: 12 }} />
                  <Line type="monotone" dataKey="nps" stroke="#E07A5F" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-[#EAE7E0] bg-white p-5" data-testid="chart-ratings">
            <div className="text-sm font-semibold text-[#1F2937] mb-3">Rating per kategori (rata-rata)</div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={ratingBars} margin={{ top: 5, right: 10, left: -10, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE7E0" />
                <XAxis dataKey="dim" tick={{ fontSize: 10, fill: "#4B5563" }} angle={-18} textAnchor="end" height={60} interval={0} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 10, fill: "#4B5563" }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EAE7E0", fontSize: 12 }} />
                <Bar dataKey="value" fill="#1E3A8A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-[#EAE7E0] bg-white p-5" data-testid="topics-pros">
              <div className="text-sm font-semibold text-[#1F2937] mb-3">Kata kunci sering di <span className="text-[#059669]">Pros</span></div>
              {data.top_pros_topics.length === 0 && <div className="text-xs text-[#4B5563]">Belum cukup data review.</div>}
              <div className="flex flex-wrap gap-2">
                {data.top_pros_topics.map((t) => (
                  <span key={t.word} className="px-3 py-1.5 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] text-xs text-[#065F46]">
                    {t.word} <span className="text-[10px] text-[#4B5563] ml-1">×{t.count}</span>
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-[#EAE7E0] bg-white p-5" data-testid="topics-cons">
              <div className="text-sm font-semibold text-[#1F2937] mb-3">Kata kunci sering di <span className="text-[#B91C1C]">Cons</span></div>
              {data.top_cons_topics.length === 0 && <div className="text-xs text-[#4B5563]">Belum cukup data review.</div>}
              <div className="flex flex-wrap gap-2">
                {data.top_cons_topics.map((t) => (
                  <span key={t.word} className="px-3 py-1.5 rounded-full bg-[#FEF2F2] border border-[#FECACA] text-xs text-[#991B1B]">
                    {t.word} <span className="text-[10px] text-[#4B5563] ml-1">×{t.count}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
